import * as React from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { rem } from '../../lib/rem';
import { Icon } from './Icon';

/**
 * Kit Checkbox — no @radix-ui/react-checkbox installed in this repo
 * (worktree can't npm install), so this is a real `role="checkbox"`
 * button with a visual 18px box inside a 32px hit area (§4 Fitts's-law
 * floor), tokens only, full keyboard support via native <button>.
 *
 * The box is sized with rem() — never raw px — so it follows the interface
 * dial AND a surface-scoped `--sf-u` (the DataTable's Aa slider), exactly
 * like the table body text it sits beside (DESIGN.md §3.6).
 */
export interface CheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Partial state — some of the things this box stands for are checked
   *  (a table's select-all with only some rows picked). Announces as
   *  `aria-checked="mixed"` and draws a dash instead of a tick. */
  indeterminate?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      checked,
      onCheckedChange,
      indeterminate,
      disabled,
      id,
      className,
      onClick,
      onMouseDown,
      ...aria
    },
    ref
  ) => (
    <button
      ref={ref}
      id={id}
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      disabled={disabled}
      onMouseDown={onMouseDown}
      onClick={(e) => {
        onClick?.(e);
        if (!disabled) onCheckedChange(!checked);
      }}
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors duration-fast',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-surface-alt',
        className
      )}
      {...aria}
    >
      <span
        style={{ height: rem(18), width: rem(18) }}
        className={cn(
          'flex shrink-0 items-center justify-center rounded border-2 transition-colors duration-fast',
          checked || indeterminate
            ? 'border-primary bg-primary'
            : 'border-border-strong bg-transparent'
        )}
      >
        {indeterminate ? (
          <Icon icon={Minus} em={0.9231} className="text-inverse" strokeWidth={3} />
        ) : (
          checked && <Icon icon={Check} em={0.9231} className="text-inverse" strokeWidth={3} />
        )}
      </span>
    </button>
  )
);
Checkbox.displayName = 'Checkbox';
